import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Modal, Form, Spinner, Row, Col } from 'react-bootstrap';
import { Reagent } from '../types';
import { addReagent, updateReagent, deleteReagent } from '../services/api';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { RmFileDropZone } from '../components/RmFileDropZone';
import { resolveStockItemImageSrc } from '../utils/resolveUploadAssetUrl';
import { DEFAULT_REAGENT_IMAGE } from '../constants/defaultImages';
import './MemberManagement.css';

const PAGE_SIZE = 10;

const ReagentStock: React.FC<{
  data: Reagent[];
  setData: React.Dispatch<React.SetStateAction<Reagent[]>>;
  loading: boolean;
  onRefresh: () => void;
  canManage?: boolean;
}> = ({ data, setData: _setData, loading, onRefresh, canManage = true }) => {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<Reagent | null>(null);
  const [form, setForm] = useState<Partial<Reagent>>({});
  const [uploadPreview, setUploadPreview] = useState('');
  const [previewModal, setPreviewModal] = useState(false);
  const [previewImg, setPreviewImg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [filterUnit, setFilterUnit] = useState('全部');
  const [filterCategory, setFilterCategory] = useState('全部');
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setForm(editing);
      setUploadPreview(
        editing.image ? resolveStockItemImageSrc(editing.image, DEFAULT_REAGENT_IMAGE) : ''
      );
    } else {
      setForm({});
      setUploadPreview('');
    }
  }, [editing, show]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploadPreview(ev.target?.result as string);
        setForm((f) => ({ ...f, image: ev.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!canManage) return;
    if (!form.cas || !form.vendor || !form.unit) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        category: form.category === '公共药品' || form.category === '小组药品' ? form.category : '小组药品',
        restockThreshold: form.restockThreshold == null ? null : Number(form.restockThreshold)
      };
      if (editing && editing._id) {
        const res = await updateReagent(editing._id, payload);
        if (res && 'error' in res && res.error) {
          throw new Error(String(res.error));
        }
      } else {
        const res = await addReagent(payload);
        if (res && 'error' in res && res.error) {
          throw new Error(String(res.error));
        }
      }
      setShow(false);
      setEditing(null);
      onRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存失败';
      alert(message === '无权限' ? '无权限编辑试剂库存' : message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: Reagent) => {
    setEditing(item);
    setShow(true);
  };

  const handleDelete = async (id: string) => {
    if (!canManage) return;
    const target = data.find((d) => d._id === id);
    if (target?.sourceApplicationId) {
      alert('该试剂由审批通过后入库，不可删除。仅可删除手动添加的库存。');
      return;
    }
    if (!window.confirm('确定要删除该试剂吗？仅手动添加的库存可删除。')) return;
    setDeletingId(id);
    try {
      const res = await deleteReagent(id);
      if (res && 'error' in res && res.error) {
        throw new Error(String(res.error));
      }
      onRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : '删除失败';
      alert(message === '无权限' ? '无权限删除试剂' : message || '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  const units = Array.from(new Set(data.map((d) => d.unit).filter(Boolean)));
  const categories = Array.from(
    new Set(data.map((d) => (d.category === '公共药品' || d.category === '小组药品' ? d.category : '小组药品')))
  );
  const filteredAll = useMemo(() => {
    return data.filter((item) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (item.cas || '').toLowerCase().includes(q) ||
        (item.catalogNumber || '').toLowerCase().includes(q) ||
        (item.vendor || '').toLowerCase().includes(q) ||
        (item.spec || '').toLowerCase().includes(q) ||
        (item.purchaser || '').toLowerCase().includes(q) ||
        (item.name || '').toLowerCase().includes(q);
      const matchUnit = filterUnit === '全部' || item.unit === filterUnit;
      const matchCategory =
        filterCategory === '全部' ||
        (item.category === '公共药品' || item.category === '小组药品' ? item.category : '小组药品') ===
          filterCategory;
      return matchSearch && matchUnit && matchCategory;
    });
  }, [data, search, filterUnit, filterCategory]);
  const total = filteredAll.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageSlice = filteredAll.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  return (
    <>
      <header className="rm-section-head">
        <div>
          <h2 className="rm-section-title">试剂库存管理</h2>
          <p className="rm-section-sub">维护试剂 CAS、厂家、规格与库存，与试剂展示数据同步。</p>
        </div>
        {canManage && (
          <button type="button" className="mm-btn-primary" onClick={() => { setEditing(null); setShow(true); }}>
            <LimsNavIcon name="add" sizePx={20} />
            新增试剂
          </button>
        )}
      </header>

      <div className="mm-table-shell">
        <div className="mm-table-toolbar">
          <div className="mm-filter-row">
            <span className="mm-filter-label">单位：</span>
            <div className="mm-filter-pills">
              <button
                type="button"
                className={`mm-pill ${filterUnit === '全部' ? 'is-active' : ''}`}
                onClick={() => { setFilterUnit('全部'); setPage(1); }}
              >
                全部
              </button>
              {units.map((u) => (
                <button
                  key={u}
                  type="button"
                  className={`mm-pill ${filterUnit === u ? 'is-active' : ''}`}
                  onClick={() => { setFilterUnit(u); setPage(1); }}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div className="mm-filter-row">
            <span className="mm-filter-label">试剂分类：</span>
            <div className="mm-filter-pills">
              <button
                type="button"
                className={`mm-pill ${filterCategory === '全部' ? 'is-active' : ''}`}
                onClick={() => { setFilterCategory('全部'); setPage(1); }}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`mm-pill ${filterCategory === c ? 'is-active' : ''}`}
                  onClick={() => { setFilterCategory(c); setPage(1); }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="mm-search">
            <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
            <input
              type="search"
              className="mm-search-input"
              placeholder="CAS、厂家、规格、分类、购买人…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              aria-label="搜索试剂"
            />
          </div>
          <div className="mm-toolbar-meta">
            第 <strong>{safePage}</strong> / {totalPages} 页 · 本页 <strong>{pageSlice.length}</strong> 条 · 共 {total} 条
            <div className="mm-mini-nav">
              <button type="button" aria-label="上一页" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                <LimsNavIcon name="chevron_left" sizePx={20} />
              </button>
              <button type="button" aria-label="下一页" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
                <LimsNavIcon name="chevron_right" sizePx={20} />
              </button>
            </div>
          </div>
        </div>
        <div className="mm-sheet-scroll">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">加载中...</p>
            </div>
          ) : (
            <table className="mm-sheet">
              <thead>
                <tr>
                  <th>图片</th>
                  <th>试剂名称</th>
                  <th>CAS号</th>
                  <th>货号</th>
                  <th>试剂分类</th>
                  <th>品牌</th>
                  <th>规格</th>
                  <th>单位</th>
                  <th>单价</th>
                  <th>库存</th>
                  <th>位置</th>
                  <th>购买日期</th>
                  <th>签收人</th>
                  <th>购买人</th>
                  <th>补货阈值</th>
                  {canManage && <th className="mm-th-actions">操作</th>}
                </tr>
              </thead>
              <tbody>
                {pageSlice.length === 0 ? (
                  <tr>
                    <td colSpan={canManage ? 16 : 15} className="mm-empty">
                      暂无匹配的试剂
                    </td>
                  </tr>
                ) : (
                  pageSlice.map((item) => (
                    <tr key={item._id || item.key} className="mm-row">
                      <td>
                        <img
                          className="mm-instrument-thumb"
                          src={resolveStockItemImageSrc(item.image, DEFAULT_REAGENT_IMAGE)}
                          alt=""
                          onClick={() => {
                            setPreviewImg(resolveStockItemImageSrc(item.image, DEFAULT_REAGENT_IMAGE));
                            setPreviewModal(true);
                          }}
                        />
                      </td>
                      <td>
                        <p className="mm-identity-name mb-0">{item.name || '—'}</p>
                      </td>
                      <td>{item.cas}</td>
                      <td>{item.catalogNumber || '—'}</td>
                      <td>{item.category?.trim() || '未分类'}</td>
                      <td>{item.vendor || '—'}</td>
                      <td>{item.spec || '—'}</td>
                      <td>{item.unit}</td>
                      <td>{item.unitPrice != null ? `¥${item.unitPrice}` : '—'}</td>
                      <td>{item.quantity}</td>
                      <td>{item.location || '—'}</td>
                      <td>{item.buyDate || '—'}</td>
                      <td>{item.receiver || '—'}</td>
                      <td>{item.purchaser || '—'}</td>
                      <td>{item.restockThreshold ?? '未设置'}</td>
                      {canManage && (
                        <td>
                          <div className="mm-actions">
                            <button type="button" className="mm-icon-action" aria-label="编辑" onClick={() => handleEdit(item)}>
                              <LimsNavIcon name="edit_note" sizePx={22} />
                            </button>
                            {!item.sourceApplicationId && (
                              <button
                                type="button"
                                className="mm-icon-action mm-icon-action--danger"
                                aria-label="删除"
                                title="删除手动添加的库存"
                                onClick={() => item._id && handleDelete(item._id)}
                                disabled={deletingId === item._id}
                              >
                                {deletingId === item._id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <LimsNavIcon name="delete" sizePx={22} />
                                )}
                              </button>
                            )}
                            {item.sourceApplicationId ? (
                              <span className="small text-muted px-1" title="关联业务入库不可删除">
                                {item.sourceAfterSalesRequestId ? '售后换货' : '审批入库'}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal show={previewModal} onHide={() => setPreviewModal(false)} centered>
        <Modal.Body style={{ textAlign: 'center' }}>
          <img src={previewImg} alt="reagent-large" style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 8 }} />
        </Modal.Body>
      </Modal>
      <Modal show={show} onHide={() => setShow(false)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? '编辑试剂' : '新增试剂'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>试剂图片</Form.Label>
              <RmFileDropZone
                inputRef={fileInputRef}
                previewSrc={uploadPreview}
                placeholderSrc={DEFAULT_REAGENT_IMAGE}
                onChange={handleImageChange}
                title="点击选择或更换图片"
                hint="支持 JPEG、PNG，建议小于 2MB"
                aria-label="选择试剂图片"
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>试剂名称</Form.Label>
                  <Form.Control value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>CAS号</Form.Label>
                  <Form.Control value={form.cas || ''} onChange={(e) => setForm({ ...form, cas: e.target.value })} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>货号</Form.Label>
                  <Form.Control
                    value={form.catalogNumber || ''}
                    onChange={(e) => setForm({ ...form, catalogNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>试剂分类</Form.Label>
                  <Form.Select
                    value={form.category === '公共药品' || form.category === '小组药品' ? form.category : '小组药品'}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    <option value="公共药品">公共药品</option>
                    <option value="小组药品">小组药品</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>品牌</Form.Label>
                  <Form.Control value={form.vendor || ''} onChange={(e) => setForm({ ...form, vendor: e.target.value })} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>规格</Form.Label>
                  <Form.Control value={form.spec || ''} onChange={(e) => setForm({ ...form, spec: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>单位</Form.Label>
                  <Form.Control value={form.unit || ''} onChange={(e) => setForm({ ...form, unit: e.target.value })} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>库存数量</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.quantity || 0}
                    onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
                    required
                    min={0}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>单价（元）</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.unitPrice ?? ''}
                    placeholder="选填"
                    onChange={(e) =>
                      setForm({
                        ...form,
                        unitPrice: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>补货阈值（非必填）</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={form.restockThreshold ?? ''}
                    placeholder="留空则不提醒补货"
                    onChange={(e) =>
                      setForm({
                        ...form,
                        restockThreshold: e.target.value ? Math.max(1, Number(e.target.value)) : undefined
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>存放位置</Form.Label>
                  <Form.Control value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>购买日期</Form.Label>
                  <Form.Control type="date" value={form.buyDate || ''} onChange={(e) => setForm({ ...form, buyDate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>签收人</Form.Label>
                  <Form.Control value={form.receiver || ''} onChange={(e) => setForm({ ...form, receiver: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>购买人</Form.Label>
                  <Form.Control value={form.purchaser || ''} onChange={(e) => setForm({ ...form, purchaser: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '确定'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReagentStock;
